/**
 * ColumnRenamer — Tools > Column Renamer (Phase 1: harvest + dictionary).
 *
 * Builds the GLOBAL case-only column-rename dictionary. Pick a connection and a
 * scope (whole connection, or one database), harvest the distinct case-sensitive
 * column names, set the canonical spelling for the ones you want to normalise,
 * and save. The SQL-editor autoformat immediately starts re-casing typed columns
 * to the saved canonical spelling.
 *
 * Only CASE changes are accepted (kodeBrg → KodeBrg) — a row whose new name
 * differs by more than case is skipped, because MySQL resolves columns
 * case-insensitively (so a case-only rename can't break views/triggers/app code)
 * and the formatter can only safely apply case changes.
 *
 * "Preview ALTERs" generates the bulk CHANGE COLUMN statements for the saved
 * dictionary into an editable block (review/edit, then copy or — Phase 3 — run).
 *
 * Props: onClose()
 */
import { createSignal, createResource, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { api } from '../lib/api.js';
import Modal from './Modal.jsx';
import CodeMirrorSQL from './CodeMirrorSQL.jsx';
import { harvestColumns, saveRenameMap, previewRenames, executeRenames, loadColumnCaseMap, columnCaseMap } from '../lib/columnrename.js';
import { splitStatements } from '../lib/sqlsplit.js';
import { toast } from '../lib/toast.js';

const SYSTEM_DBS = ['mysql', 'information_schema', 'performance_schema', 'sys'];

export default function ColumnRenamer(props) {
  const [conns] = createResource(async () => {
    const { connections } = await api.get('/api/connections');
    return connections ?? [];
  });
  const [connId, setConnId]   = createSignal('');
  const [scope, setScope]     = createSignal('connection'); // 'connection' | 'database'
  const [dbList, setDbList]   = createSignal(null);
  const [dbLoading, setDbLoading] = createSignal(false);
  const [database, setDatabase]   = createSignal('');
  const [rows, setRows]       = createStore([]); // [{ match, name }]
  const [filter, setFilter]   = createSignal('');
  const [harvesting, setHarvesting] = createSignal(false);
  const [saving, setSaving]   = createSignal(false);
  const [err, setErr]         = createSignal('');
  const [previewing, setPreviewing] = createSignal(false);
  const [previewSql, setPreviewSql] = createSignal(null); // null = not previewed yet
  const [previewMeta, setPreviewMeta] = createSignal({ count: 0, tables: 0 });
  const [onError, setOnError] = createSignal('stop'); // 'stop' | 'continue'
  const [confirmRun, setConfirmRun] = createSignal(false);
  const [running, setRunning] = createSignal(false);
  const [runResults, setRunResults] = createSignal(null); // { results, ran, failed, ok }

  // Statements parsed from the (editable) preview text, ready to run.
  const pendingStatements = () => splitStatements(previewSql() || '').map((s) => s.text).filter(Boolean);

  // A real, case-ONLY change (the only kind we save / highlight green).
  const isCaseOnly = (match, name) => {
    const n = (name || '').trim();
    return !!n && n !== match && n.toLowerCase() === match.toLowerCase();
  };
  // A change of more than case (rejected — flagged red).
  const isBadChange = (match, name) => {
    const n = (name || '').trim();
    return !!n && n.toLowerCase() !== match.toLowerCase();
  };

  const loadDbs = async (id) => {
    if (!id) { setDbList(null); return; }
    setDbLoading(true);
    try {
      const { databases } = await api.get(`/api/connections/${id}/databases`);
      setDbList((databases ?? []).map((d) => d.name).filter((n) => !SYSTEM_DBS.includes(n)));
    } catch (e) { setErr(e.message); setDbList([]); }
    finally { setDbLoading(false); }
  };

  const onConnChange = (id) => {
    setConnId(id);
    setDatabase('');
    setRows([]);
    if (scope() === 'database') loadDbs(id);
  };
  const onScopeChange = (s) => {
    setScope(s);
    if (s === 'database' && connId()) loadDbs(connId());
  };

  const harvest = async () => {
    if (!connId()) { setErr('Pick a connection first.'); return; }
    if (scope() === 'database' && !database()) { setErr('Pick a database.'); return; }
    setErr(''); setHarvesting(true);
    try {
      const cols = await harvestColumns(connId(), scope() === 'database' ? database() : '');
      const dict = columnCaseMap();
      setRows(cols.map((m) => ({ match: m, name: (dict && dict.get(m.toLowerCase())) || m })));
      if (!cols.length) toast.info('No columns found in scope.');
    } catch (e) { setErr(e.message); }
    finally { setHarvesting(false); }
  };

  // Rows the filter shows, carrying their real index so edits write back correctly.
  const visibleRows = () => {
    const f = filter().trim().toLowerCase();
    const out = [];
    rows.forEach((r, i) => { if (!f || r.match.toLowerCase().includes(f)) out.push({ r, i }); });
    return out;
  };

  // How many rows are a real (case-only) change.
  const changedCount = () => rows.filter((r) => isCaseOnly(r.match, r.name)).length;

  const save = async () => {
    const pairs = [];
    let skipped = 0;
    for (const r of rows) {
      if (isCaseOnly(r.match, r.name)) pairs.push({ match: r.match, canonical: r.name.trim() });
      else if (isBadChange(r.match, r.name)) skipped++;
    }
    if (skipped > 0) toast.warn(`${skipped} row(s) skipped — only case changes are allowed (e.g. kodeBrg → KodeBrg).`);
    if (!pairs.length) { toast.info('No case-only changes to save.'); return; }
    setSaving(true);
    try {
      const r = await saveRenameMap(pairs);
      await loadColumnCaseMap(); // refresh the global dictionary → editors re-case live
      toast.success(`Saved ${r.saved ?? pairs.length} rule(s). The SQL editor autoformat now follows them.`);
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };

  // Generate the bulk CHANGE COLUMN preview from the SAVED dictionary, against the
  // current connection/scope. (Save first — preview reads the stored dictionary.)
  const preview = async () => {
    if (!connId()) { setErr('Pick a connection first.'); return; }
    if (scope() === 'database' && !database()) { setErr('Pick a database.'); return; }
    setErr(''); setPreviewing(true);
    try {
      const r = await previewRenames(connId(), scope() === 'database' ? database() : '');
      setPreviewSql((r.statements || []).join('\n'));
      setPreviewMeta({ count: r.count || 0, tables: r.tables || 0 });
      if (!r.count) toast.info('No columns match the saved dictionary in this scope.');
    } catch (e) { setErr(e.message); }
    finally { setPreviewing(false); }
  };

  const copyPreview = async () => {
    try { await navigator.clipboard.writeText(previewSql() || ''); toast.success('Copied.'); }
    catch { toast.warn('Copy failed — select the text and copy manually.'); }
  };

  // Run the (edited) preview statements on the connection. Schema-mutating, so
  // it's gated behind an explicit confirm bar.
  const runRenames = async () => {
    setConfirmRun(false);
    const statements = pendingStatements();
    if (!statements.length) { toast.info('Nothing to run.'); return; }
    setRunning(true); setRunResults(null);
    try {
      const r = await executeRenames(connId(), statements, onError());
      setRunResults(r);
      if (r.ok) toast.success(`Renamed: ${r.ran} statement(s) ran, 0 failed.`);
      else toast.error(`${r.failed} of ${r.ran} statement(s) failed — see results below.`);
      // Re-preview so the list reflects what's left (renamed columns drop out).
      preview();
    } catch (e) { toast.error(`Run failed: ${e.message}`); }
    finally { setRunning(false); }
  };

  return (
    <Modal open={true} onClose={props.onClose} title="Column Renamer" wide>
      <div class="col-renamer">
        <p class="cr-intro muted">
          Build the global case-only column-naming dictionary. Harvested names whose
          casing you change here are normalised everywhere the SQL-editor autoformat runs.
        </p>

        {/* Step 1 — pick the source. Card-style block with a clear label so the
            user knows where each control belongs. */}
        <section class="cr-step">
          <header class="cr-step-head">
            <span class="cr-step-num">1</span>
            <span class="cr-step-title">Pick the source</span>
          </header>
          <div class="cr-step-body">
            <div class="cr-grid">
              <label class="cr-field">
                <span class="cr-field-label">Connection</span>
                <select class="input" value={connId()} onChange={(e) => onConnChange(e.currentTarget.value)}>
                  <option value="">— pick a connection —</option>
                  <For each={conns() ?? []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
                </select>
              </label>
              <div class="cr-field">
                <span class="cr-field-label">Scope</span>
                <div class="cr-scope-row">
                  <label class="cr-radio" classList={{ active: scope() === 'connection' }}>
                    <input type="radio" name="cr-scope" checked={scope() === 'connection'}
                           onChange={() => onScopeChange('connection')} />
                    <span>Whole connection</span>
                    <small class="muted">all databases except system</small>
                  </label>
                  <label class="cr-radio" classList={{ active: scope() === 'database' }}>
                    <input type="radio" name="cr-scope" checked={scope() === 'database'}
                           onChange={() => onScopeChange('database')} />
                    <span>Single database</span>
                    <small class="muted">just one</small>
                  </label>
                </div>
              </div>
              <Show when={scope() === 'database'}>
                <label class="cr-field">
                  <span class="cr-field-label">Database</span>
                  <select class="input" value={database()} onChange={(e) => setDatabase(e.currentTarget.value)}
                          disabled={dbLoading() || !dbList()}>
                    <option value="">{dbLoading() ? 'loading…' : '— pick a database —'}</option>
                    <For each={dbList() ?? []}>{(n) => <option value={n}>{n}</option>}</For>
                  </select>
                </label>
              </Show>
            </div>
            <div class="cr-step-actions">
              <button class="btn" onClick={harvest} disabled={harvesting() || !connId()}>
                {harvesting() ? '⏳ Getting columns…' : '⟳ Get column names'}
              </button>
            </div>
          </div>
        </section>

        <Show when={err()}>
          <div class="form-error" style="margin:8px 0;">{err()}</div>
        </Show>

        <Show when={rows.length > 0}>
          {/* Step 2 — review + edit the dictionary. The table is the centerpiece;
              filter + counts sit above it; save / preview are right-aligned. */}
          <section class="cr-step">
            <header class="cr-step-head">
              <span class="cr-step-num">2</span>
              <span class="cr-step-title">Edit the dictionary</span>
              <span class="cr-step-meta muted">{rows.length} columns · {changedCount()} will change</span>
            </header>
            <div class="cr-step-body">
              <div class="cr-table-toolbar">
                <input class="input cr-filter" placeholder="🔍 Filter columns…" value={filter()}
                       onInput={(e) => setFilter(e.currentTarget.value)} />
                <div class="spacer" />
                <button class="btn btn-ghost" onClick={preview} disabled={previewing() || !connId()}
                        title="Generate the CHANGE COLUMN statements for the SAVED dictionary">
                  {previewing() ? '⏳ Generating…' : '⎙ Preview ALTERs'}
                </button>
                <button class="btn" onClick={save} disabled={saving() || changedCount() === 0}>
                  {saving() ? '⏳ Saving…' : `💾 Save dictionary${changedCount() ? ` (${changedCount()})` : ''}`}
                </button>
              </div>
              <div class="col-renamer-table-wrap">
                <table class="col-renamer-table">
                  <thead>
                    <tr>
                      <th>Match column name</th>
                      <th>→</th>
                      <th>New column name</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={visibleRows()}>
                      {({ r, i }) => (
                        <tr classList={{ 'cr-changed': isCaseOnly(r.match, rows[i].name), 'cr-bad': isBadChange(r.match, rows[i].name) }}>
                          <td class="mono">{r.match}</td>
                          <td class="cr-arrow muted">→</td>
                          <td>
                            <input class="input mono" value={rows[i].name}
                                   onInput={(e) => setRows(i, 'name', e.currentTarget.value)}
                                   title={isBadChange(r.match, rows[i].name) ? 'Only case may change (e.g. kodeBrg → KodeBrg)' : ''} />
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </Show>

        <Show when={previewSql() !== null}>
          <section class="cr-step cr-step-preview">
            <header class="cr-step-head">
              <span class="cr-step-num">3</span>
              <span class="cr-step-title">Preview & run ALTERs</span>
              <span class="cr-step-meta muted">{previewMeta().count} statement{previewMeta().count === 1 ? '' : 's'} · {previewMeta().tables} table{previewMeta().tables === 1 ? '' : 's'}</span>
            </header>
            <div class="cr-step-body">
            <div class="cr-table-toolbar">
              <button class="btn btn-ghost" onClick={copyPreview} disabled={!previewSql()}>⧉ Copy</button>
              <div class="spacer" />
              <label class="cr-onerror" title="On the first failure: stop, or skip it and keep going">
                <span class="muted">On error:</span>
                <select class="input" value={onError()} onChange={(e) => setOnError(e.currentTarget.value)}>
                  <option value="stop">Stop</option>
                  <option value="continue">Skip &amp; continue</option>
                </select>
              </label>
              <button class="btn btn-danger-solid" onClick={() => setConfirmRun(true)}
                      disabled={running() || !previewSql()}>
                {running() ? '⏳ Running…' : '▶ Run'}
              </button>
            </div>

            <Show when={previewSql()} fallback={<div class="muted" style="padding:8px 0;">Nothing to rename in this scope.</div>}>
              <CodeMirrorSQL value={previewSql()} onInput={setPreviewSql} maxHeight="240px" lineWrap={false} />
            </Show>

            <Show when={confirmRun()}>
              <div class="cr-confirm">
                <span>⚠ Run {pendingStatements().length} statement(s) against <strong>{conns()?.find((c) => String(c.id) === String(connId()))?.name}</strong>? This alters the live schema.</span>
                <div class="spacer" />
                <button class="btn btn-sm btn-ghost" onClick={() => setConfirmRun(false)}>Cancel</button>
                <button class="btn btn-sm btn-danger-solid" onClick={runRenames}>Confirm run</button>
              </div>
            </Show>

            <Show when={runResults()}>
              <div class="cr-results">
                <div class="muted" style="margin:6px 0 4px;">
                  {runResults().ran} ran · {runResults().failed} failed
                </div>
                <For each={runResults().results.filter((x) => !x.ok)}>
                  {(x) => <div class="cr-result-err mono"><span>✗</span> {x.error}<div class="cr-result-sql">{x.sql}</div></div>}
                </For>
              </div>
            </Show>

            <p class="cr-footnote muted">
              Definitions are taken verbatim from each table's <code>SHOW CREATE TABLE</code>, so the
              <code> CHANGE COLUMN</code> is faithful. Editable — review before running. Each statement is audit-logged.
            </p>
            </div>
          </section>
        </Show>

        <Show when={previewSql() === null && rows.length > 0}>
          <p class="cr-footnote muted">
            Only case changes are saved (a different-case variant of the same name). <strong>Save</strong> the
            dictionary, then <strong>Preview ALTERs</strong> to generate the bulk <code>CHANGE COLUMN</code> statements.
          </p>
        </Show>
      </div>
    </Modal>
  );
}
